/**
 * Finance (REQ-019).
 *
 * Stripe is the merchant of record, so this screen is smaller than a finance
 * system would otherwise be — Stripe sells, collects the tax, invoices the
 * customer and remits. What is left is our own books, and the first figure on
 * the page is the one that is easiest to get wrong: income is what settled,
 * not what customers were charged. Charging gross includes tax that was never
 * ours and fees that never reached us.
 *
 * Every figure opens to what composes it, because REQ-019 AC-009 says a figure
 * that cannot be opened is not displayed.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Banknote,
  CalendarClock,
  Loader2,
  Plus,
  Wallet,
} from "lucide-react";
import { useState } from "react";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import apiClient from "@/services/api/apiClient";

interface Money {
  amount: number;
  currency: string;
}

interface Payout {
  id: string;
  amount: number;
  currency: string;
  status: string;
  arrivalDate: string | null;
  createdAt: string;
  bankMatchedAt: string | null;
  bankReference: string | null;
}

interface Overview {
  stripe: {
    balance: { available: Money[]; pending: Money[] };
    payouts: Payout[];
    unmatched: number;
  };
  deferred: {
    total: { cash: number; earned: number; deferred: number };
    contracts: number;
    unpriced: number;
  };
  payables: {
    overdue: number;
    dueSoon: number;
    /** One total per currency owed in — nothing here converts between them. */
    committedNext30Days: Money[];
  };
  grt: { period: string; dueOn: string; state: string };
}

const formatMoney = (locale: string, minor: number, currency: string) =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(minor / 100);

function Card({
  label,
  value,
  hint,
  tone = "normal",
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "normal" | "warn";
}) {
  return (
    <div
      className={`rounded-lg border p-4 ${
        tone === "warn"
          ? "border-amber-200 bg-amber-50"
          : "border-gray-200 bg-white"
      }`}
    >
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
        {value}
      </p>
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

export default function FinancePage() {
  const fmt = useLocaleFormat();
  const money = (minor: number, currency = "usd") =>
    formatMoney(fmt.locale, minor, currency);
  const queryClient = useQueryClient();
  const [recording, setRecording] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [matching, setMatching] = useState<string | null>(null);

  const overview = useQuery({
    queryKey: ["finance", "overview"],
    queryFn: async () => {
      const response = await apiClient.get<{ data: Overview }>(
        "/staff/finance/overview",
      );
      return response.data.data;
    },
    retry: false,
  });

  const said = (error: unknown, fallback: string) =>
    (error as { response?: { data?: { error?: { message?: string } } } })
      ?.response?.data?.error?.message ?? fallback;

  const match = useMutation({
    mutationFn: async ({
      id,
      reference,
    }: {
      id: string;
      reference: string;
    }) => {
      await apiClient.post(`/staff/finance/payouts/${id}/match`, { reference });
    },
    onSuccess: () => {
      setMatching(null);
      void queryClient.invalidateQueries({ queryKey: ["finance"] });
    },
    onError: (error) => setRefusal(said(error, "Could not record the match.")),
  });

  const record = useMutation({
    mutationFn: async (form: FormData) => {
      await apiClient.post("/staff/finance/payables", {
        supplier: String(form.get("supplier") ?? "").trim(),
        reference: String(form.get("reference") ?? "").trim(),
        // Entered in whole currency, stored in minor units: a float here is a
        // rounding error that compounds through every total on the page.
        amount: Math.round(Number(form.get("amount")) * 100),
        currency: String(form.get("currency") ?? "usd"),
        dueOn: String(form.get("dueOn") ?? ""),
        category: String(form.get("category") ?? "").trim() || undefined,
      });
    },
    onSuccess: () => {
      setRefusal(null);
      setRecording(false);
      void queryClient.invalidateQueries({ queryKey: ["finance"] });
    },
    onError: (error) =>
      setRefusal(said(error, "Could not record the invoice.")),
  });

  const data = overview.data;
  const stripeDown =
    (overview.error as { response?: { status?: number } })?.response?.status ===
    503;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <Wallet className="h-5 w-5 text-sky-600" /> Finance
        </h1>
        <button
          onClick={() => setRecording((open) => !open)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700"
        >
          <Plus className="h-4 w-4" /> Record an invoice
        </button>
      </div>

      <p className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
        Stripe is the merchant of record: it sells, collects the tax, invoices
        the customer and remits. Income below is what <em>settled</em> to us,
        net of Stripe's fees and with no customer tax in it — charging gross
        would count somebody else's tax liability as our revenue.
      </p>

      {refusal && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{refusal}</p>
        </div>
      )}

      {overview.isLoading ? (
        <div className="flex items-center gap-2 p-8 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : stripeDown ? (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Stripe is not configured on this server, so nothing can be read from
            it. The payables below do not depend on it.
          </p>
        </div>
      ) : !data ? (
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-red-700">
          Finance could not be read.
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Card
              label="Stripe holds"
              value={data.stripe.balance.available
                .map((b) => money(b.amount, b.currency))
                .join(" · ")}
              hint={`plus ${data.stripe.balance.pending
                .map((b) => money(b.amount, b.currency))
                .join(" · ")} not yet cleared`}
            />
            <Card
              label="Revenue earned"
              value={money(data.deferred.total.earned)}
              hint={`${money(data.deferred.total.deferred)} still owed as service`}
            />
            <Card
              label="Committed, 30 days"
              value={
                // One figure per currency, like the Stripe card above: a
                // single total would have to convert, and nothing does.
                data.payables.committedNext30Days.length === 0
                  ? money(0)
                  : data.payables.committedNext30Days
                      .map((owed) => money(owed.amount, owed.currency))
                      .join(" · ")
              }
              hint={`${data.payables.overdue} overdue, ${data.payables.dueSoon} due within a week`}
              tone={data.payables.overdue > 0 ? "warn" : "normal"}
            />
            <Card
              label="Payouts unmatched"
              value={String(data.stripe.unmatched)}
              hint="Stripe says sent; the bank has not been ticked off"
              tone={data.stripe.unmatched > 0 ? "warn" : "normal"}
            />
          </div>

          {data.deferred.unpriced > 0 && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              {data.deferred.unpriced} subscriptions could not be priced and are
              excluded from the earned and deferred figures — counted rather
              than valued at zero, which would understate what we owe.
            </p>
          )}

          <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4 text-sm">
            <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
            <p className="text-gray-600">
              New Mexico gross receipts tax — period {data.grt.period}, due{" "}
              {fmt.date(data.grt.dueOn)} ({data.grt.state}
              ).
            </p>
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
              <Banknote className="h-4 w-4 text-gray-400" />
              <h2 className="text-sm font-medium text-gray-900">
                Payouts, and whether they reached the bank
              </h2>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Sent</th>
                  <th className="px-4 py-2 font-medium">Amount</th>
                  <th className="px-4 py-2 font-medium">Stripe</th>
                  <th className="px-4 py-2 font-medium">Bank</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.stripe.payouts.map((payout) => (
                  <tr key={payout.id}>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                      {fmt.date(payout.createdAt)}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-gray-900">
                      {money(payout.amount, payout.currency)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{payout.status}</td>
                    <td className="px-4 py-3">
                      {payout.bankMatchedAt ? (
                        <span className="text-emerald-700">
                          matched · {payout.bankReference}
                        </span>
                      ) : matching === payout.id ? (
                        <form
                          className="flex gap-1"
                          onSubmit={(event) => {
                            event.preventDefault();
                            const reference = new FormData(
                              event.currentTarget,
                            ).get("reference");
                            match.mutate({
                              id: payout.id,
                              reference: String(reference ?? ""),
                            });
                          }}
                        >
                          <input
                            name="reference"
                            placeholder="Bank line reference"
                            required
                            className="w-40 rounded border border-gray-300 px-2 py-1 text-xs"
                          />
                          <button
                            type="submit"
                            className="rounded bg-sky-600 px-2 py-1 text-xs text-white"
                          >
                            Save
                          </button>
                        </form>
                      ) : (
                        <button
                          onClick={() => setMatching(payout.id)}
                          className="text-sky-700 hover:underline"
                        >
                          Match to a bank line
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="border-t border-gray-100 px-4 py-2 text-xs text-gray-500">
              There is no bank feed, so matching is done by a person reading the
              statement. Stripe saying “paid” means the payout left Stripe, not
              that it arrived.
            </p>
          </div>
        </>
      )}

      {recording && (
        <form
          className="grid gap-3 rounded-lg border border-gray-200 bg-white p-4 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            record.mutate(new FormData(event.currentTarget));
          }}
        >
          <label className="text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              Supplier
            </span>
            <input
              name="supplier"
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              Invoice number
            </span>
            <input
              name="reference"
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium text-gray-700">Amount</span>
            <input
              name="amount"
              type="number"
              step="0.01"
              min="0.01"
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium text-gray-700">Due on</span>
            <input
              name="dueOn"
              type="date"
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>
          <div className="flex gap-2 sm:col-span-2">
            <button
              type="submit"
              disabled={record.isPending}
              className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-60"
            >
              Record
            </button>
            <button
              type="button"
              onClick={() => setRecording(false)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
